import React, { useEffect, useState, useRef } from "react";
import api, { API } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Slider } from "../components/ui/slider";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "../components/ui/accordion";
import { Upload, Download, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { TESTIDS } from "../lib/testids";
import {
  Card, Button, SectionLabel,
} from "../components/app/theme";
import UsersSection from "../components/app/UsersSection";

const TEMPLATE_LABELS = {
  relance_douce: "Relance douce (7-14 j)",
  relance_ferme: "Relance ferme (14-30 j)",
  derniere_relance: "Dernière relance (> 30 j)",
  reactivation_dormant: "Réactivation client dormant",
  signal_urgent: "Signal urgent",
};

function normalizeWeights(w) {
  const total = w.poids_montant + w.poids_anciennete + w.poids_relances + w.poids_interaction;
  if (total <= 0) return w;
  const k = 100 / total;
  return {
    poids_montant: Math.round(w.poids_montant * k),
    poids_anciennete: Math.round(w.poids_anciennete * k),
    poids_relances: Math.round(w.poids_relances * k),
    poids_interaction: Math.round(w.poids_interaction * k),
  };
}

export default function Settings() {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === "admin";
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState({ nb_propales: 0, nb_clients: 0 });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const propalesRef = useRef();
  const clientsRef = useRef();

  const load = async () => {
    const [s, st] = await Promise.all([api.get("/settings"), api.get("/data/stats")]);
    setSettings(s.data);
    setStats(st.data);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", settings);
      toast.success("Réglages enregistrés");
    } catch { toast.error("Échec"); }
    finally { setSaving(false); }
  };

  const upload = async (kind, file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post(`/import/${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${r.data.imported} ${kind} importées`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Import échoué");
    } finally { setUploading(false); }
  };

  const exportCsv = async (kind) => {
    const token = localStorage.getItem("pc_token");
    const r = await fetch(`${API}/export/${kind}.csv`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await r.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${kind}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const sampleCsv = (kind) => {
    window.open(`${API}/sample/${kind}.csv`, "_blank");
  };

  const resetData = async () => {
    if (!window.confirm("Supprimer toutes les données importées ?")) return;
    await api.post("/data/reset");
    toast.success("Données supprimées");
    load();
  };

  const resetSettings = async () => {
    if (!window.confirm("Réinitialiser tous les réglages ?")) return;
    const r = await api.post("/settings/reset");
    setSettings(r.data);
    toast.success("Réglages réinitialisés");
  };

  if (!settings) {
    return (
      <div className="p-8 text-sm" style={{ color: "var(--th-text-3)" }}>
        Chargement…
      </div>
    );
  }

  const w = normalizeWeights({
    poids_montant: settings.poids_montant,
    poids_anciennete: settings.poids_anciennete,
    poids_relances: settings.poids_relances,
    poids_interaction: settings.poids_interaction,
  });
  const totalPoids = w.poids_montant + w.poids_anciennete + w.poids_relances + w.poids_interaction;

  return (
    <div className="th-scope p-6 md:p-10 max-w-4xl" style={{ backgroundColor: "var(--th-bg)" }}>
      <header className="mb-8">
        <SectionLabel>Configuration</SectionLabel>
        <h1
          className="mt-1"
          style={{
            fontFamily: "var(--th-font-serif)",
            fontSize: 27,
            fontWeight: 500,
            letterSpacing: "-0.005em",
            color: "var(--th-text)",
          }}
        >
          Réglages
        </h1>
        <p className="text-sm mt-1" style={{ color: "var(--th-text-2)" }}>
          Toutes les règles d&apos;analyse sont paramétrables ici. Aucune IA, aucun ML.
        </p>
      </header>

      <Accordion type="multiple" defaultValue={["source", "scoring", "templates", "users"]} className="space-y-3">
        {/* Source de données */}
        <AccordionItem value="source" className="th-card" style={{ border: "1px solid var(--th-border)" }}>
          <AccordionTrigger className="px-5 py-4 hover:no-underline">
            <div className="text-left">
              <SectionLabel>1. Source de données</SectionLabel>
              <div
                className="mt-1"
                style={{
                  fontFamily: "var(--th-font-serif)",
                  fontSize: 18,
                  fontWeight: 500,
                  color: "var(--th-text)",
                }}
              >
                CSV — Pipedrive (à venir)
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-5">
            <div className="grid md:grid-cols-2 gap-4">
              <Card className="p-4">
                <div className="text-sm mb-3" style={{ color: "var(--th-text)", fontWeight: 500 }}>
                  Import Propales ({stats.nb_propales} en base)
                </div>
                <input ref={propalesRef} type="file" accept=".csv" hidden onChange={(e) => upload("propales", e.target.files[0])} />
                <div className="flex flex-col gap-2">
                  <Button
                    data-testid={TESTIDS.importPropalesBtn}
                    onClick={() => propalesRef.current?.click()}
                    disabled={uploading}
                    variant="secondary"
                    className="w-full"
                  >
                    <Upload size={14} /> Importer propales.csv
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="secondary" className="flex-1 h-9" onClick={() => sampleCsv("propales")}>
                      <Download size={12} /> Exemple
                    </Button>
                    <Button variant="secondary" className="flex-1 h-9" onClick={() => exportCsv("propales")}>
                      <Download size={12} /> Export
                    </Button>
                  </div>
                </div>
              </Card>
              <Card className="p-4">
                <div className="text-sm mb-3" style={{ color: "var(--th-text)", fontWeight: 500 }}>
                  Import Clients ({stats.nb_clients} en base)
                </div>
                <input ref={clientsRef} type="file" accept=".csv" hidden onChange={(e) => upload("clients", e.target.files[0])} />
                <div className="flex flex-col gap-2">
                  <Button
                    data-testid={TESTIDS.importClientsBtn}
                    onClick={() => clientsRef.current?.click()}
                    disabled={uploading}
                    variant="secondary"
                    className="w-full"
                  >
                    <Upload size={14} /> Importer clients.csv
                  </Button>
                  <div className="flex gap-2">
                    <Button variant="secondary" className="flex-1 h-9" onClick={() => sampleCsv("clients")}>
                      <Download size={12} /> Exemple
                    </Button>
                    <Button variant="secondary" className="flex-1 h-9" onClick={() => exportCsv("clients")}>
                      <Download size={12} /> Export
                    </Button>
                  </div>
                </div>
              </Card>
            </div>

            <div
              className="mt-4 p-4"
              style={{
                border: "1px dashed var(--th-border-strong)",
                backgroundColor: "var(--th-surface-soft)",
                borderRadius: 12,
                opacity: 0.75,
              }}
            >
              <div className="text-sm mb-1" style={{ color: "var(--th-text)", fontWeight: 500 }}>
                Pipedrive (lecture seule)
              </div>
              <div className="text-xs" style={{ color: "var(--th-text-2)" }}>
                À configurer — token API à renseigner dans les variables d&apos;environnement.
                Aucune écriture ne sera jamais effectuée. Après connexion, vous mapperez les
                étapes « propale commerciale envoyée / gagné / perdu ».
              </div>
            </div>

            <div className="mt-4 flex gap-2">
              <Button variant="secondary" onClick={resetData} style={{ color: "var(--th-haut-text)", borderColor: "var(--th-haut-bg)" }}>
                <Trash2 size={14} /> Vider les données
              </Button>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Scoring */}
        <AccordionItem value="scoring" className="th-card" style={{ border: "1px solid var(--th-border)" }}>
          <AccordionTrigger className="px-5 py-4 hover:no-underline">
            <div className="text-left">
              <SectionLabel>2. Pondérations du scoring de relance</SectionLabel>
              <div
                className="mt-1 th-num"
                style={{
                  fontFamily: "var(--th-font-serif)",
                  fontSize: 18,
                  fontWeight: 500,
                  color: "var(--th-text)",
                }}
              >
                Total = {totalPoids} %
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-5 space-y-5">
            {[
              ["poids_montant", "Montant"],
              ["poids_anciennete", "Ancienneté"],
              ["poids_relances", "Nombre de relances"],
              ["poids_interaction", "Jours depuis dernière interaction"],
              ["poids_conversion", "Taux de conversion observé (bonus, désactivé par défaut)"],
            ].map(([k, label]) => (
              <div key={k}>
                <div className="flex justify-between text-sm mb-1" style={{ color: "var(--th-text)" }}>
                  <span>{label}</span>
                  <span className="th-num">{settings[k]} %</span>
                </div>
                <Slider
                  data-testid={`slider-${k}`}
                  min={0} max={100} step={1}
                  value={[settings[k]]}
                  onValueChange={([v]) => setSettings({ ...settings, [k]: v })}
                />
              </div>
            ))}

            <div className="pt-4" style={{ borderTop: "1px solid var(--th-border)" }}>
              <div className="text-sm mb-2" style={{ color: "var(--th-text)" }}>
                Seuil d&apos;anomalie de cycle (multiplicateur)
              </div>
              <div className="flex items-center gap-4">
                <Slider
                  min={1} max={3} step={0.1}
                  value={[settings.seuil_anomalie]}
                  onValueChange={([v]) => setSettings({ ...settings, seuil_anomalie: v })}
                  className="flex-1"
                />
                <span className="th-num text-sm w-16 text-right">
                  ×{Number(settings.seuil_anomalie).toFixed(1)}
                </span>
              </div>
              <p className="text-xs mt-1" style={{ color: "var(--th-text-3)" }}>
                Alerte si silence &gt; {Number(settings.seuil_anomalie).toFixed(1)} × cycle habituel.
              </p>
            </div>

            <div className="pt-4" style={{ borderTop: "1px solid var(--th-border)" }}>
              <div className="text-sm mb-2" style={{ color: "var(--th-text)" }}>
                Fenêtre d&apos;impact (jours glissants)
              </div>
              <div className="flex items-center gap-4">
                <Slider
                  min={7} max={90} step={1}
                  value={[settings.impact_window_days || 30]}
                  onValueChange={([v]) => setSettings({ ...settings, impact_window_days: v })}
                  className="flex-1"
                />
                <span className="th-num text-sm w-16 text-right">
                  {settings.impact_window_days || 30} j
                </span>
              </div>
              <p className="text-xs mt-1" style={{ color: "var(--th-text-3)" }}>
                Période sur laquelle le bandeau « Impact » agrège vos relances et signatures.
              </p>
            </div>
          </AccordionContent>
        </AccordionItem>

        {/* Templates */}
        <AccordionItem value="templates" className="th-card" style={{ border: "1px solid var(--th-border)" }}>
          <AccordionTrigger className="px-5 py-4 hover:no-underline">
            <div className="text-left">
              <SectionLabel>3. Templates de messages</SectionLabel>
              <div
                className="mt-1"
                style={{
                  fontFamily: "var(--th-font-serif)",
                  fontSize: 18,
                  fontWeight: 500,
                  color: "var(--th-text)",
                }}
              >
                5 modèles modifiables
              </div>
            </div>
          </AccordionTrigger>
          <AccordionContent className="px-5 pb-5 space-y-4">
            <div
              className="text-xs p-3"
              style={{
                border: "1px solid var(--th-border)",
                backgroundColor: "var(--th-surface-soft)",
                borderRadius: 8,
                color: "var(--th-text-2)",
              }}
            >
              Variables disponibles : {"{contact_nom}"}, {"{prenom}"}, {"{entreprise}"}, {"{numero}"}, {"{montant}"}, {"{date_envoi}"}, {"{jours_depuis_envoi}"}, {"{derniere_commande_date}"}, {"{cycle_habituel}"}, {"{montant_annuel}"}
            </div>
            {Object.keys(TEMPLATE_LABELS).map((key) => (
              <div key={key}>
                <div className="text-sm mb-1" style={{ color: "var(--th-text)", fontWeight: 500 }}>
                  {TEMPLATE_LABELS[key]}
                </div>
                <Textarea
                  data-testid={`tpl-${key}`}
                  rows={5}
                  value={settings.templates?.[key] || ""}
                  onChange={(e) => setSettings({
                    ...settings,
                    templates: { ...settings.templates, [key]: e.target.value },
                  })}
                  style={{
                    border: "1px solid var(--th-border-strong)",
                    borderRadius: 8,
                    backgroundColor: "var(--th-surface)",
                    fontSize: 13,
                    color: "var(--th-text)",
                  }}
                />
              </div>
            ))}
          </AccordionContent>
        </AccordionItem>

        {/* Utilisateurs (admin only) */}
        {isAdmin && (
          <AccordionItem value="users" className="th-card" style={{ border: "1px solid var(--th-border)" }}>
            <AccordionTrigger className="px-5 py-4 hover:no-underline">
              <div className="text-left">
                <SectionLabel>4. Utilisateurs</SectionLabel>
                <div
                  className="mt-1"
                  style={{
                    fontFamily: "var(--th-font-sans)",
                    fontSize: 18,
                    fontWeight: 500,
                    color: "var(--th-text)",
                  }}
                >
                  Gérer les accès des testeurs
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="px-5 pb-5">
              <UsersSection currentUserId={currentUser?.user_id} />
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button
          data-testid="settings-save"
          onClick={save}
          disabled={saving}
          variant="primary"
        >
          {saving ? "Enregistrement…" : "Enregistrer les réglages"}
        </Button>
        <Button variant="secondary" onClick={resetSettings}>
          <RotateCcw size={14} /> Valeurs par défaut
        </Button>
      </div>
    </div>
  );
}
